import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, ArrowUp, ArrowDown, Eye, ExternalLink } from "lucide-react";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { api, errMsg } from "@/lib/api";
import { useSite } from "@/context/SiteContext";
import { Fields, PageTitle, Badge, Thumb, btnPrimary, btnGhost } from "./ui";

export default function CrudManager({ col, title, sub, schema, defaults = {}, display, publicPath }) {
  const [items, setItems] = useState(null);
  const [edit, setEdit] = useState(null);
  const [del, setDel] = useState(null);
  const [saving, setSaving] = useState(false);
  const { reload } = useSite();
  const load = () => api.get(`/admin/c/${col}`).then((r) => setItems(r.data));
  useEffect(() => { load(); }, [col]); // eslint-disable-line react-hooks/exhaustive-deps

  const save = async () => {
    setSaving(true);
    try {
      if (edit.id) await api.put(`/admin/c/${col}/${edit.id}`, edit);
      else await api.post(`/admin/c/${col}`, edit);
      toast.success("Saved");
      setEdit(null);
      load();
      reload();
    } catch (e) { toast.error(errMsg(e)); }
    setSaving(false);
  };
  const remove = async () => {
    await api.delete(`/admin/c/${col}/${del.id}`);
    toast.success("Deleted");
    setDel(null);
    load();
    reload();
  };
  const move = async (i, d) => {
    const c = [...items]; const j = i + d;
    if (j < 0 || j >= c.length) return;
    [c[i], c[j]] = [c[j], c[i]];
    setItems(c);
    await api.post(`/admin/c/${col}/reorder`, { ids: c.map((x) => x.id) });
    reload();
  };
  const d = display || ((x) => ({ title: x.title || x.name || x.question, sub: x.short || x.text || x.answer || x.description, image: x.cover || x.card_image || x.poster }));

  return (
    <div data-testid={`crud-${col}`}>
      <PageTitle title={title} sub={sub}>
        <button className={btnPrimary} onClick={() => setEdit({ ...defaults })} data-testid={`crud-${col}-add-btn`}><Plus size={16} /> Add new</button>
      </PageTitle>
      {!items ? <p>Loading…</p> : items.length === 0 ? <p className="bg-white p-8 text-center text-black/60">Nothing here yet. Click “Add new” to create your first one.</p> : (
        <ul className="divide-y divide-black/5 border border-black/10 bg-white">
          {items.map((x, i) => {
            const v = d(x);
            return (
              <li key={x.id} className="flex items-center gap-4 p-3" data-testid={`crud-${col}-row-${i}`}>
                <div className="flex flex-col"><button onClick={() => move(i, -1)} aria-label="Move up" className="text-black/40 hover:text-black"><ArrowUp size={14} /></button><button onClick={() => move(i, 1)} aria-label="Move down" className="text-black/40 hover:text-black"><ArrowDown size={14} /></button></div>
                {v.image !== undefined && (v.image ? <Thumb url={v.image} className="h-14 w-20 shrink-0" /> : <div className="h-14 w-20 shrink-0 bg-black/5" />)}
                <div className="min-w-0 flex-1">
                  <p className="truncate font-semibold">{v.title || "(untitled)"} {x.featured && <span className="ml-2 text-xs font-bold uppercase text-[color:var(--ib-orange)]">★ Homepage</span>}</p>
                  <p className="truncate text-sm text-black/55">{v.sub}</p>
                </div>
                {x.status && <Badge s={x.status} />}
                {publicPath && x.slug && ["published", "approved"].includes(x.status) && <a href={`${publicPath}/${x.slug}`} target="_blank" rel="noreferrer" className="p-2 text-black/50 hover:text-black" aria-label="View on site"><ExternalLink size={16} /></a>}
                <button className={btnGhost} onClick={() => setEdit({ ...x })} data-testid={`crud-${col}-edit-${i}`}><Pencil size={14} /> Edit</button>
                <button className="p-2 text-red-600" onClick={() => setDel(x)} aria-label="Delete" data-testid={`crud-${col}-delete-${i}`}><Trash2 size={16} /></button>
              </li>
            );
          })}
        </ul>
      )}
      <Sheet open={!!edit} onOpenChange={(o) => !o && setEdit(null)}>
        <SheetContent className="w-full overflow-y-auto sm:max-w-3xl" data-testid={`crud-${col}-editor`}>
          <SheetHeader><SheetTitle className="font-display text-2xl uppercase">{edit?.id ? "Edit" : "New"} {title.replace(/s$/, "")}</SheetTitle></SheetHeader>
          {edit && <div className="mt-6"><Fields schema={schema} value={edit} onChange={setEdit} testid={`${col}-field`} /></div>}
          {edit?.admin_note && <p className="mt-4 bg-amber-50 p-3 text-xs text-amber-800">{edit.admin_note}</p>}
          <div className="sticky bottom-0 mt-8 flex gap-3 border-t bg-white py-4">
            <button className={btnPrimary} onClick={save} disabled={saving} data-testid={`crud-${col}-save-btn`}>{saving ? "Saving…" : "Save"}</button>
            <button className={btnGhost} onClick={() => setEdit(null)}>Cancel</button>
            {publicPath && edit?.slug && <a className={btnGhost} href={`${publicPath}/${edit.slug}`} target="_blank" rel="noreferrer"><Eye size={14} /> Preview</a>}
          </div>
        </SheetContent>
      </Sheet>
      <AlertDialog open={!!del} onOpenChange={(o) => !o && setDel(null)}>
        <AlertDialogContent>
          <AlertDialogHeader><AlertDialogTitle>Delete this item?</AlertDialogTitle><AlertDialogDescription>“{del && d(del).title}” will be permanently removed. To hide it instead, set its status to Draft.</AlertDialogDescription></AlertDialogHeader>
          <AlertDialogFooter><AlertDialogCancel>Cancel</AlertDialogCancel><AlertDialogAction onClick={remove} className="bg-red-600" data-testid="confirm-delete-btn">Delete</AlertDialogAction></AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
